import React from 'react';

import {
  Card, Container, TextualLink, Tooltip,
} from '@hvcgroep/design-system';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CustomerDetails from '@hvcgroep/design-system/assets/illustrations/Klantgegevens.svg';
import Contract from '@hvcgroep/design-system/assets/illustrations/Termijnbedrag.svg';
import GeoThermie from '@hvcgroep/design-system/assets/illustrations/Geothermie.svg';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import SkeletonLoader from '../../components/SkeletonLoader';
import useContract from '../../hooks/useContract';
import ContractSwitcherHeader from '../../containers/ContractSwitcherHeader';
import formatAddress from '../../helpers/formatAddress';
import ErrorPage from '../ErrorPage';

import './style.scss';

import DefinitionList from '../../components/DefinitionList';
import StatusIcon from '../../components/StatusIcon';
import useDownloadDocument from '../../hooks/useDownloadDocument';

type TariffCode = 'CW3' | 'CW4' | 'CW5' | 'CW6' | 'GT' | 'ZW';

const MyDetails: React.FC = () => {
  const {
    selectedContract,
    isLoading,
    selectedCustomer,
    selectedAccount,
    isError,
  } = useContract();

  const { t } = useTranslation();

  const { downloadDocument } = useDownloadDocument();
  const heading = t('pages.myDetails.title');
  const siteTitle = t('general.siteTitle');

  const hasEndDate = !!selectedContract?.endDate;

  if (isError) {
    return <ErrorPage type="serverError" />;
  }

  const status = selectedContract?.status;
  const isEditable = status !== 'FINALIZED' && status !== 'TERMINATED';

  return (
    <Container fluid>
      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>
      {/* @TODO only show the skeleton loader when the page is loading, and check whether the right type is being used */}
      {isLoading && !selectedContract && (
        <SkeletonLoader type="my-contract" />
      )}
      {selectedContract && (
        <>
          <ContractSwitcherHeader heading={heading} />
          {selectedAccount && (
            <div className="mo-my-details-content">
              <Card
                heading={t('pages.myDetails.customerDetails')}
                image={CustomerDetails}
              >
                <DefinitionList>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('customerDetails.name')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {selectedAccount.contacts?.[0]?.firstName} {selectedAccount.contacts?.[0]?.lastName}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('customerDetails.email')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {selectedAccount.contacts?.[0]?.email}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('customerDetails.phone')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {selectedAccount.contacts?.[0]?.phone}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('customerDetails.iban')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {selectedAccount.financial?.bankAccountNumber}&nbsp;
                      {isEditable ? (
                        <span>
                          (
                          <TextualLink asChild className="font-weight-normal">
                            <Link to="/betaalgegevens-wijzigen">{t('advancePayment.amendPaymentDetails')}</Link>
                          </TextualLink>
                          )
                        </span>
                      ) : (
                        <div className="d-flex">
                          <p className="text-bold text-muted">({t('advancePayment.amendPaymentDetails')})</p>
                          <Tooltip title={t('pages.changePaymentDetails.notEditable.paymentDetails')} tooltipId="tooltip_desc" />
                        </div>
                      )}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                </DefinitionList>
              </Card>
              <Card
                heading={t('pages.myDetails.contract')}
                image={Contract}
                actionButton={(
                  <TextualLink
                    onClick={() => {
                      downloadDocument({
                        documentType: 'contract',
                        alternateId: selectedCustomer?.alternateId,
                        documentId: selectedContract?.agreement?.id ?? "",
                      });
                    }}
                  >
                    {t('pages.myDetails.downloadContract')}
                  </TextualLink>
                )}
              >
                <span className="text-bold">
                  {formatAddress(selectedContract.address!)}
                </span>

                <DefinitionList>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('pages.myDetails.status')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      <ContractStatus status={selectedContract.status} />
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('pages.myDetails.contractId')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {selectedContract.agreement?.id}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('pages.myDetails.startDate')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {format(new Date(selectedContract.startDate!), 'dd-MM-yyyy')}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  {hasEndDate && (
                    <DefinitionList.Row>
                      <DefinitionList.Term>
                        {t('pages.myDetails.endDate')}
                      </DefinitionList.Term>
                      <DefinitionList.Details>
                        {format(new Date(selectedContract.endDate!), 'dd-MM-yyyy')}
                      </DefinitionList.Details>
                    </DefinitionList.Row>
                  )}
                </DefinitionList>
                <p className="currentRates text-small m-b-0">
                  <Trans
                    i18nKey="pages.myDetails.content.textCurrentRates"
                  >
                    <CurrentRates />
                  </Trans>
                </p>
              </Card>

              <Card
                heading={t('pages.myDetails.connection')}
                image={GeoThermie}
              >
                <DefinitionList>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('pages.myDetails.standardPeriodQuantities')}
                    </DefinitionList.Term>
                    {selectedContract.masterData?.standardPeriodQuantities?.[0] && (
                      <DefinitionList.Details>
                        {selectedContract.masterData?.standardPeriodQuantities[0].amount}{' '}{selectedContract.masterData?.standardPeriodQuantities[0].unit}
                      </DefinitionList.Details>
                    )}
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('pages.myDetails.smartMeter')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {selectedContract.masterData?.hasSmartMeter ? t('pages.myDetails.hasSmartMeter.true') : t('pages.myDetails.hasSmartMeter.false')}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                  <DefinitionList.Row>
                    <DefinitionList.Term>
                      {t('pages.myDetails.capacityTariff')}
                    </DefinitionList.Term>
                    <DefinitionList.Details>
                      {t(`pages.myDetails.capacityTariffCodes.${selectedContract.masterData?.capacityTariffCode as TariffCode}`)}
                    </DefinitionList.Details>
                  </DefinitionList.Row>
                </DefinitionList>
              </Card>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

/**
 * A React Component to handle a nested <Trans> component that takes in a
 * `<Slot />` using the `asChild` prop.
 */
function CurrentRates({
  children,
}: {
  children?: React.ReactNode
}) {
  const { t } = useTranslation();

  return (
    <a href="https://www.hvcgroep.nl/warmte/alles-over-warmtetarieven">{children}</a>
  );
}


function ContractStatus({ status }: { status?: string }) {
  const { t } = useTranslation();

  if (status === 'ACTIVE') {
    return (
      <>
        <StatusIcon
          useColoredText
          textPosition="before"
          text={t('pages.myDetails.statuses.ACTIVE')}
          status="success"
        />
      </>
    );
  }

  if (status === 'PENDING' || status === 'NEW' || status === 'ENROLLED') {
    return (
      <>
        <StatusIcon
          useColoredText
          textPosition="before"
          text={t('pages.myDetails.statuses.PENDING')}
          status="pending"
        />
      </>
    );
  }

  if (status === 'PENDING_FINAL' || status === 'FINALIZED' || status === 'TERMINATED') {
    return (
      <>
        <StatusIcon
          useColoredText
          textPosition="before"
          text={t('pages.myDetails.statuses.END')}
          status="end"
        />
      </>
    );
  }

  return null;
}

export default MyDetails;
